import { useState } from 'react';
import { Button, IconSave, IconTrash, Loader } from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';

import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { usePagePublish } from 'contexts/page-publish-dto/usePagePublish';
import useIsNewSectionPage from 'hooks/useIsNewSectionPage';
import { getPublishButtonVariant, PublishButton } from './components/publish-button/PublishButton';
import styles from './PagePublishingButtons.module.scss';
import { EmbargoPagePublishDialog } from '../embargo-page-publish-dialog/EmbargoPagePublishDialog';
import { SchedulePageDialog } from '../schedule-page-dialog/SchedulePageDialog';

export const PagePublishingButtons = () => {
  const { getEmbargoContent, queriesHaveSettled } = useDataModelContext();
  const isCreatingNewPage = useIsNewSectionPage();
  const {
    canSave,
    originalPageWhenInHistoryEditMode,
    isPublishing,
    handleSaveDraft,
    isSavingDraft,
    isScheduling,
    isRescheduling,
    isUpdatingSchedule,
    isDeleting,
    hasPageChanged,
    handlePublish,
    handleSchedule,
    handleReschedule,
    handleUpdateSchedule,
    handleDeleteSchedule,
    handleHideHistoryEditMode,
    createNewSection
  } = usePagePublish();

  const queryClient = useQueryClient();

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showEmbargoModal, setShowEmbargoModal] = useState(false);
  const isHistoryEdit = !!originalPageWhenInHistoryEditMode;

  const handleSave = async () => {
    await handleUpdateSchedule();
  };

  const onSchedule = async (publishUtc: number) => {
    if (isHistoryEdit) {
      await handleReschedule(publishUtc);
    } else {
      await handleSchedule(publishUtc);
    }

    setShowScheduleModal(false);
  };

  const toggleScheduleDialog = () => {
    setShowScheduleModal((prevState) => !prevState);
  };

  const toggleEmbargoDialog = () => {
    setShowEmbargoModal((prevState) => !prevState);
  };

  const handleDeleteScheduledPublish = async () => {
    await handleDeleteSchedule();
  };

  const embargoedContent = getEmbargoContent();
  const hasEmbargoedContent = embargoedContent.length > 0;
  const variant = getPublishButtonVariant({ hasEmbargoedContent, isHistoryEdit });

  const pageIsScheduling = isScheduling || isRescheduling;

  const pageIsLoading = isPublishing || isUpdatingSchedule || isSavingDraft || pageIsScheduling || isDeleting;

  const saveDisabled = pageIsLoading || !canSave;

  const disabled = pageIsLoading || !hasPageChanged;

  const deleteScheduleDisabled = pageIsLoading;

  const publishAndScheduleDisabled = pageIsLoading || !queriesHaveSettled;

  const scheduleButtonLabel = isHistoryEdit ? 'Reschedule' : 'Schedule';

  return (
    <>
      {showScheduleModal && (
        <SchedulePageDialog onSchedule={onSchedule} onCancel={toggleScheduleDialog} isLoading={pageIsScheduling} />
      )}
      {showEmbargoModal && (
        <EmbargoPagePublishDialog
          embargoedContent={embargoedContent}
          onPublish={isHistoryEdit ? handleSave : handlePublish}
          onCancel={toggleEmbargoDialog}
        />
      )}
      <div className={styles.publishingButtons}>
        {isHistoryEdit && (
          <Button
            tertiary
            onClick={handleDeleteScheduledPublish}
            disabled={deleteScheduleDisabled || isCreatingNewPage}
            margin={{ right: 'sm' }}
            color="lava"
            icon={IconTrash}
            data-testid="page-delete-schedule-button"
          >
            {isDeleting && <Loader size="md" margin={{ right: 'md' }} />}
            Delete Scheduled publish
          </Button>
        )}
        {isHistoryEdit && (
          <Button
            secondary
            onClick={handleHideHistoryEditMode}
            disabled={disabled || isCreatingNewPage}
            margin={{ right: 'sm' }}
            data-testid="page-cancel-edit-button"
          >
            Cancel Edit
          </Button>
        )}
        {!isHistoryEdit && (
          <Button
            tertiary
            onClick={handleSaveDraft}
            disabled={saveDisabled || isCreatingNewPage}
            data-testid="page-save-draft-button"
            margin={{ right: 'md' }}
            icon={IconSave}
          >
            {isSavingDraft && <Loader size="md" margin={{ right: 'md' }} />}
            Save Draft
          </Button>
        )}
        <Button
          margin={{ left: 'sm' }}
          secondary
          onClick={toggleScheduleDialog}
          disabled={publishAndScheduleDisabled || isCreatingNewPage}
          data-testid="page-schedule-button"
        >
          {pageIsScheduling && <Loader size="md" margin={{ right: 'md' }} />}
          {scheduleButtonLabel}
        </Button>
        {isCreatingNewPage ? (
          <Button
            primary
            onClick={createNewSection}
            disabled={Boolean(queryClient.isMutating())}
            data-testid="page-create-new-page-button"
            margin={{ left: 'sm' }}
          >
            {Boolean(queryClient.isMutating()) && <Loader size="md" margin={{ right: 'md' }} />}
            Create
          </Button>
        ) : (
          <PublishButton
            variant={variant}
            disabled={publishAndScheduleDisabled}
            publishFunctions={{ handlePublishImmediately: handlePublish, handleSave, toggleEmbargoDialog }}
            isLoading={isPublishing || isUpdatingSchedule}
          />
        )}
      </div>
    </>
  );
};
